/**
 * Ornithos Theme - Main JavaScript
 */

(function() {
  'use strict';

  // ====================================
  // Utility Functions
  // ====================================

  const debounce = (fn, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const formatMoney = (cents, format = '{{amount}} €') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '{{amount}} €';

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = (cents / 100).toFixed(2).replace('.', ',');
        break;
      case 'amount_no_decimals':
        value = Math.floor(cents / 100);
        break;
      case 'amount_with_space_separator':
        value = (cents / 100).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  // ====================================
  // Fade Up Animation on Scroll
  // ====================================

  const initFadeUpAnimations = () => {
    const elements = document.querySelectorAll('.fade-up');
    
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(el => observer.observe(el));
  };

  // ====================================
  // Mobile Menu
  // ====================================

  const initMobileMenu = () => {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    const close = document.querySelector('[data-mobile-menu-close]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');

    if (!toggle || !menu) return;

    const openMenu = () => {
      menu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
    };

    const closeMenu = () => {
      menu.classList.remove('is-open');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.contains('is-open');
      isOpen ? closeMenu() : openMenu();
    });

    if (close) close.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  };

  // ====================================
  // Header Scroll Effect
  // ====================================

  const initHeaderScroll = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;

    const handleScroll = () => {
      if (window.scrollY > 50) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', debounce(handleScroll, 10));
    handleScroll();
  };

  const updateHeaderHeight = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const height = header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  };

  // ====================================
  // Quantity Selector
  // ====================================

  const initQuantitySelectors = () => {
    document.querySelectorAll('.quantity-selector').forEach(selector => {
      const minus = selector.querySelector('[data-quantity-minus]');
      const plus = selector.querySelector('[data-quantity-plus]');
      const input = selector.querySelector('[data-quantity-input]');

      if (!minus || !plus || !input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 999;
      const line = input.dataset.line;

      const normalizeQuantity = (value) => {
        let quantity = parseInt(value, 10);
        if (Number.isNaN(quantity)) quantity = min;
        if (quantity < min) quantity = min;
        if (quantity > max) quantity = max;
        return quantity;
      };

      minus.addEventListener('click', () => {
        const current = parseInt(input.value);
        if (current > min) {
          input.value = current - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      plus.addEventListener('click', () => {
        const current = parseInt(input.value);
        if (current < max) {
          input.value = current + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      if (line) {
        input.addEventListener('change', debounce(() => {
          const quantity = normalizeQuantity(input.value);
          if (parseInt(input.value, 10) !== quantity) {
            input.value = quantity;
          }
          updateCartLine(parseInt(line, 10), quantity);
        }, 200));
      }
    });
  };

  // ====================================
  // Add to Cart
  // ====================================

  const initAddToCart = () => {
    document.querySelectorAll('[data-add-to-cart]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const buttons = Array.from(form.querySelectorAll('[data-product-submit], [type="submit"]'));
        const defaultLabel = buttons[0]?.dataset.defaultLabel || buttons[0]?.textContent.trim() || 'Ajouter au panier';
        const loadingLabel = buttons[0]?.dataset.loadingLabel || 'Ajout en cours…';
        const successLabel = buttons[0]?.dataset.successLabel || 'Ajouté ✓';
        const errorLabel = buttons[0]?.dataset.errorLabel || 'Erreur';

        const setButtonsState = (label, disabled) => {
          buttons.forEach((button) => {
            const labelNode = button.querySelector('[data-add-to-cart-label]');
            button.disabled = disabled;
            if (labelNode) {
              labelNode.textContent = label;
            } else {
              button.textContent = label;
            }
          });
        };

        setButtonsState(loadingLabel, true);

        const formData = new FormData(form);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });
          
          const item = await response.json();

          if (response.ok) {
            setButtonsState(successLabel, true);
            
            // Update cart count
            updateCartCount();

            // Refresh main cart (utile pour l'ajout rapide depuis la page panier)
            refreshMainCart();

            // Open cart drawer if exists
            const cartDrawer = document.querySelector('[data-cart-drawer]');
            if (cartDrawer) {
              cartDrawer.classList.add('is-open');
              refreshCartDrawer();
            }
            
            setTimeout(() => {
              setButtonsState(defaultLabel, false);
            }, 2000);
          } else {
            throw new Error(item.description || 'Erreur lors de l\'ajout');
          }
        } catch (error) {
          console.error('Add to cart error:', error);
          setButtonsState(errorLabel, true);
          setTimeout(() => {
            setButtonsState(defaultLabel, false);
          }, 2000);
        }
      });
    });
  };

  // ====================================
  // Cart Functions
  // ====================================

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  };

  const refreshMainCart = async () => {
    const mainCart = document.querySelector('[data-main-cart]');
    if (!mainCart) return;

    try {
      const response = await fetch('/?section_id=main-cart');
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-main-cart]');

      if (newContent) {
        mainCart.innerHTML = newContent.innerHTML;
        initQuantitySelectors();
      }
    } catch (error) {
      console.error('Error refreshing main cart:', error);
    }
  };

  const refreshCartDrawer = async () => {
    const drawer = document.querySelector('[data-cart-drawer-content]');
    if (!drawer) return;

    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      
      if (newContent) {
        drawer.innerHTML = newContent.innerHTML;
        initQuantitySelectors();
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  };

  const updateCartLine = async (line, quantity) => {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) {
        throw new Error('Cart update failed');
      }

      await response.json();
      updateCartCount();
      refreshCartDrawer();
      refreshMainCart();
    } catch (error) {
      console.error('Error updating cart line:', error);
    }
  };

  // ====================================
  // Cart Drawer
  // ====================================

  const initCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const toggles = document.querySelectorAll('[data-cart-toggle]');
    const close = document.querySelector('[data-cart-drawer-close]');
    const overlay = document.querySelector('[data-cart-drawer-overlay]');

    if (!drawer) return;

    const openDrawer = () => {
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    toggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = drawer.classList.contains('is-open');
        isOpen ? closeDrawer() : openDrawer();
      });
    });

    if (close) close.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });
  };

  // ====================================
  // Product Gallery
  // ====================================

  const initProductGallery = () => {
    const mainImage = document.querySelector('[data-product-main-image]');
    const thumbnails = document.querySelectorAll('[data-product-thumbnail]');

    if (!mainImage || !thumbnails.length) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.dataset.fullImage || thumb.src;
        mainImage.src = newSrc;
        
        thumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  };

  // ====================================
  // Variant Selector
  // ====================================

  const initVariantSelector = () => {
    const selectors = document.querySelectorAll('[data-variant-selector]');
    
    selectors.forEach(selector => {
      selector.addEventListener('change', () => {
        const form = selector.closest('form');
        if (!form) return;
        const productSection = form.closest('.main-product') || document;

        const allSelectors = form.querySelectorAll('[data-variant-selector]');
        const options = Array.from(allSelectors).map(s => s.value);
        
        const variantsJson = form.querySelector('[data-variants-json]');
        if (!variantsJson) return;

        const variants = JSON.parse(variantsJson.textContent);
        const selectedVariant = variants.find(v => 
          v.options.every((opt, i) => opt === options[i])
        );

        if (selectedVariant) {
          const variantInput = form.querySelector('[name="id"]');
          if (variantInput) variantInput.value = selectedVariant.id;

          // Update price
          productSection.querySelectorAll('[data-product-price]').forEach((priceEl) => {
            priceEl.textContent = formatMoney(selectedVariant.price);
          });

          // Update compare price
          productSection.querySelectorAll('[data-product-compare-price]').forEach((comparePriceEl) => {
            if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceEl.textContent = formatMoney(selectedVariant.compare_at_price);
              comparePriceEl.hidden = false;
            } else {
              comparePriceEl.hidden = true;
            }
          });

          // Update availability
          const nextLabel = selectedVariant.available ? 'Ajouter au panier' : 'Épuisé';
          productSection.querySelectorAll('[data-product-submit]').forEach((button) => {
            button.disabled = !selectedVariant.available;
            button.dataset.defaultLabel = nextLabel;
            const labelNode = button.querySelector('[data-add-to-cart-label]');
            if (labelNode) {
              labelNode.textContent = nextLabel;
            } else {
              button.textContent = nextLabel;
            }
          });

          productSection.querySelectorAll('[data-product-availability]').forEach((availabilityEl) => {
            availabilityEl.classList.toggle('stock-indicator--in-stock', selectedVariant.available);
            availabilityEl.classList.toggle('stock-indicator--out-of-stock', !selectedVariant.available);

            const labelNode = availabilityEl.querySelector('[data-product-availability-label]');
            const nextAvailability = selectedVariant.available
              ? availabilityEl.dataset.availableLabel
              : availabilityEl.dataset.unavailableLabel;

            if (labelNode && nextAvailability) {
              labelNode.textContent = nextAvailability;
            }
          });

          // Update URL
          const url = new URL(window.location);
          url.searchParams.set('variant', selectedVariant.id);
          window.history.replaceState({}, '', url);
        }
      });
    });
  };

  // ====================================
  // Smooth Scroll for Anchor Links
  // ====================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  };

  // ====================================
  // Initialize
  // ====================================

  const init = () => {
    initFadeUpAnimations();
    initMobileMenu();
    initHeaderScroll();
    updateHeaderHeight();
    window.addEventListener('resize', debounce(updateHeaderHeight, 100));
    initQuantitySelectors();
    initAddToCart();
    initCartDrawer();
    initProductGallery();
    initVariantSelector();
    initSmoothScroll();
    updateCartCount();
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utilities globally for use in sections
  window.Ornithos = {
    formatMoney,
    updateCartCount,
    refreshCartDrawer,
    refreshMainCart,
    debounce
  };

})();
